import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useVerifyEmail } from "./auth";
import { AuthShell } from "./authChrome";

type State = "checking" | "done" | "error" | "no_token";

export function VerifyEmailPage() {
  const { t } = useTranslation("auth");
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const verify = useVerifyEmail();
  const qc = useQueryClient();

  const [state, setState] = useState<State>(token ? "checking" : "no_token");
  const firedRef = useRef(false);

  useEffect(() => {
    if (!token || firedRef.current) return;
    firedRef.current = true; // guard against React 18 StrictMode double-mount
    verify.mutate(token, {
      onSuccess: () => {
        // Email is now verified server-side — drop the cached `me` so the
        // soft-banner disappears on next app load.
        qc.invalidateQueries({ queryKey: ["me"] });
        setState("done");
      },
      onError: () => setState("error"),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <AuthShell backTo="/login" backLabel={t("verify.to_login")}>
      <div className="indigo-card-glass p-6 text-center space-y-4">
        {state === "checking" && (
          <>
            <div className="flex justify-center">
              <div className="relative w-11 h-11">
                <div className="absolute inset-0 rounded-full border-2 border-bg-border" />
                <div className="absolute inset-0 rounded-full border-2 border-accent border-t-transparent animate-spin" />
              </div>
            </div>
            <div className="text-[14px] text-fg-secondary">{t("verify.checking")}</div>
          </>
        )}

        {state === "done" && (
          <>
            <div className="text-[16px] font-semibold text-fg-primary">{t("verify.done_title")}</div>
            <p className="text-[13px] text-fg-secondary leading-relaxed">{t("verify.done_text")}</p>
            <Link
              to="/"
              className="block w-full h-11 leading-[2.75rem] rounded-pill bg-accent hover:bg-accent-hover text-accent-on font-semibold transition-colors"
            >
              {t("verify.go_to_app")}
            </Link>
          </>
        )}

        {(state === "error" || state === "no_token") && (
          <>
            <div className="text-[16px] font-semibold text-fg-primary">{t("verify.err_title")}</div>
            <p className="text-[13px] text-fg-secondary leading-relaxed">
              {state === "no_token" ? t("verify.err_no_token") : t("verify.err_text")}
            </p>
            <Link
              to="/login"
              className="block w-full h-11 leading-[2.75rem] rounded-pill bg-accent hover:bg-accent-hover text-accent-on font-semibold transition-colors"
            >
              {t("verify.to_login")}
            </Link>
          </>
        )}
      </div>
    </AuthShell>
  );
}
